import React, { useContext, useState, useEffect, useMemo, memo, Suspense, Children, isValidElement, cloneElement } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios'; 
import parse from 'html-react-parser'; 
import DOMPurify from 'dompurify';
import he from 'he';
import './style.css';
import { format, parseISO } from 'date-fns';

// Lazy-loaded components
const RelatedArticles = React.lazy(() => import('./RelatedArticles'));
const RelatedArticlesTitles = React.lazy(() => import('./RelatedArticlesTitles'));
const CommentSection = React.lazy(() => import('./CommentSection'));
const MintArticle = React.lazy(() => import('./MintArticle'));

// Other components
import ErrorBoundary from './ErrorBoundary';
import ArticleSkeleton from './ArticleSkeleton'; 
import Err from '../assets/err.webp'; 
import { getOptimizedThumbnail, getSsrOptimizedThumbnail, checkAvifSupport } from './frontendUtils.js'; 
import { Link } from 'react-router-dom'; 
import usePrebid from "./prebid/usePrebid";
import { getArticleAd, getNativeAd } from "./prebid/articleAds";
import SidebarAd from "./prebid/SidebarAd"; 


/* -------------------------------------------------------------------------- */
/*                                 HELPERS                                    */
/* -------------------------------------------------------------------------- */

function preprocessHtml(html = '') {
  return html
    .replace(/<!-- wp:paragraph -->/g, '')
    .replace(/<!-- \/wp:paragraph -->/g, '')
    .replace(/\[caption[^\]]*\]/g, '')
    .replace(/\[\/caption\]/g, '')
    .replace(/[\r\n]+/g, ' ')
    .trim();
}

function addParagraphTags(html = '') {
  if (!html.includes('<p>')) {
    const normalizedHtml = html.replace(/\r\n/g, '\n');
    const paragraphs = normalizedHtml.split(/\n{2,}/);
    return paragraphs.map((paragraph) => `<p>${paragraph.trim()}</p>`).join('');
  }
  return html;
}

function useParsedHtml(html = '') {
  return useMemo(() => {
    if (!html) return null;

    const sanitizedHtml = DOMPurify.sanitize(preprocessHtml(html), {
      ADD_TAGS: ['iframe', 'figure', 'figcaption'],
      ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'src', 'width', 'height']
    });

    return parse(sanitizedHtml, {
      replace: (domNode) => {
        if (domNode.type === 'script' || domNode.type === 'form') {
          return null;
        }

        // Render iframe safely
        if (domNode.type === 'tag' && domNode.name === 'iframe') {
          const { src, width = "100%", height = "315", allow, allowfullscreen } = domNode.attribs;
          return (
            <iframe
              src={src}
              width={width}
              height={height}
              allow={allow || "accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"}
              allowFullScreen={allowfullscreen !== "false"}
              frameBorder="0"
              style={{ maxWidth: '100%', border: 'none' }}
            />
          );
        }

        if (domNode.type === 'tag') {
          let modifiedClass = domNode.attribs.class || '';
          if (['h2', 'h3', 'h4', 'h5'].includes(domNode.name)) {
            modifiedClass = `${modifiedClass} wp-block-heading heading-style heading-size-${domNode.name}`.trim();
          }
          if (domNode.name === 'figcaption') {
            modifiedClass = `${modifiedClass} wp-element-caption image-caption`.trim();
          }
          domNode.attribs.class = modifiedClass;
        }

        return domNode;
      },
    });
  }, [html]);
}


/* -------------------------------------------------------------------------- */
/*                            DATA FETCHING HOOK                              */
/* -------------------------------------------------------------------------- */

const maxRetries = 3;
let retryCount = 0;

//Extract Related Article Fetching
async function fetchRelatedArticlesTitles(categoryName, articleId) {
  try {
    const response = await axios.get(
      `/api/category/${categoryName}?per_page=4&exclude=${articleId}`
    );
    return response.data.slice(0, 4);
  } catch (err) {
    console.error(`Error fetching related articles: ${err}`);
  }
}

//Improved Error Handling and Retry Logic
function handleFetchError(error, retryFunction, setError, setLoading, navigate) {

    if (error.response?.status === 404) {
        navigate("*"); // Redirect to 404 page
    } else if (error.response?.status >= 500 && retryCount < maxRetries) {
        retryCount++;
        //console.warn(`[Article Page] Retrying request in ${200 * retryCount}ms...`);
        setTimeout(retryFunction, 200 * retryCount);
    } else {
        setError("Something went wrong. Please try again later.");
        setLoading(false);
    }
}

//Main Fetch Function
function useFetchArticle({ year, month, day, title }) {
  const navigate = useNavigate();
  const [articleData, setArticleData] = useState({
    article: null,
    thumbnail: null,
    relatedArticlesTitles: [],
    loading: true,
    isSubscriber: false,
    newVisitor: false,
    newGuest: false,
    error: null,
    isPrebidConfigured: false
  });
  
  // Only calculate once on mount, not on every render
  const [optimizedWidth] = useState(window.innerWidth <= 480 ? 400 : 800);
  const [supportsAvif, setSupportsAvif] = useState(null);
  const { isPrebidReady } = usePrebid();

  // Fetch article data
  useEffect(() => {
    let isMounted = true;
    
    async function fetchArticle() {
      if (!isMounted) return;
      
      try {
        // Check for SSR data
        const dataElement = document.getElementById("ssr-article-data"); 
        

        if (dataElement && dataElement.textContent.trim()) {
          //console.log("Using SSR data for hydration.");
          const data = JSON.parse(dataElement.textContent);

          //console.log("SSR data loaded:", data);
          
          // Clear script content immediately to free memory
          dataElement.textContent = "";
          
          // Set all initial data in a single state update
          setArticleData(prevState => ({
            ...prevState,
            article: data,
            isSubscriber: data.isSubscriber,
            newVisitor: data.newVisitor,
            newGuest: data.newGuest,
            thumbnail: getSsrOptimizedThumbnail(data.thumbnail),
            loading: false
          }));
          
          // Fetch related articles in parallel with state update
          if (data.categories?.length && isMounted) {
            const relatedArticles = await fetchRelatedArticlesTitles(data.categories[0], data.id);
            if (isMounted) {
              setArticleData(prevState => ({
                ...prevState,
                relatedArticlesTitles: relatedArticles
              }));
            }
          }
          
          return;
        }
        
        // Client-side fetch with optimized headers
        //console.log("⚡ No SSR data, fetching fresh article from API...");
        const userAddress = localStorage.getItem('userAddress');
        const selectedNetwork = localStorage.getItem('selectedNetwork');
        
        // Fetch AVIF support check in parallel with article data
        const avifPromise = checkAvifSupport().catch(() => false);
        
        const response = await axios.get(`/${year}/${month}/${day}/${title}`, {
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
            "X-User-Address": userAddress || "",
            "X-Network": selectedNetwork || "mainnet",
          },
          withCredentials: true,
        });
        
        if (!isMounted) return;
        
        const data = response.data;
        //console.log(" API article data loaded:", data);
        const avifSupported = await avifPromise;
        
        if (isMounted) {
          setSupportsAvif(avifSupported);
          
          // Compute optimized thumbnail
          const optimizedThumbnail = data?.thumbnail ? 
            getOptimizedThumbnail(data.thumbnail) : 
            null;
          
          // Update all state at once to minimize renders
          setArticleData(prevState => ({
            ...prevState,
            article: data,
            isSubscriber: data.isSubscriber,
            newVisitor: data.newVisitor,
            newGuest: data.newGuest,
            thumbnail: optimizedThumbnail,
            loading: false
          }));
          
          // Fetch related articles if available
          if (data.categories?.length && isMounted) {
            const relatedArticles = await fetchRelatedArticlesTitles(data.categories[0], data.id);

            if (isMounted) {
              setArticleData(prevState => ({
                ...prevState,
                relatedArticlesTitles: relatedArticles
              }));
            }
          }
        }
      } catch (error) {
        if (isMounted) {
          //console.error("Error fetching article:", error);
          setArticleData(prevState => ({
            ...prevState,
            error: "Failed to load article.",
            loading: false
          }));
        }
      }
    }
    
    fetchArticle();
    
    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [year, month, day, title, optimizedWidth]);
  
  // Configure Prebid separately only when needed
  useEffect(() => {
    if (!window.pbjs || !isPrebidReady || !articleData.article || articleData.isPrebidConfigured) {
      return;
    }
    
    const article = articleData.article;
    
    try {
      // Extract keywords safely
      const keywords = article?.excerpt 
      ? article.excerpt.split(" ").slice(0, 10).join(",") 
      : ""; 

    
      window.pbjs.setConfig({
        ortb2: {
          site: {
            id: article?.ID?.toString() || "unknown",
            name: article?.title?.rendered || "Untitled",
            page: window.location.href,
            domain: window.location.hostname,
            genre: "News",
            language: "en",
            sectioncat: article?.categories || [],
            keywords: keywords,
            publisher: {
              ext: {
                publish_date: article?.date || "",
                thumbnail: article?.thumbnail || "",
                image_caption: article?.imageCaption || "",
                video_embed: article?.videoEmbed || ""
              }
            }
          }
        }
      });
      
      setArticleData(prevState => ({
        ...prevState,
        isPrebidConfigured: true
      }));
      
      //console.log("Updated Prebid with new article metadata");
    } catch (error) {
      //console.error("Error configuring Prebid:", error);
    }
  }, [isPrebidReady, articleData.article, articleData.isPrebidConfigured]);
  
  // Destructure values from the consolidated state
  const { 
    article, 
    thumbnail, 
    relatedArticlesTitles, 
    loading, 
    isSubscriber, 
    newVisitor, 
    newGuest, 
    error, 
    isPrebidConfigured 
  } = articleData;
  
  return { 
    article, 
    thumbnail, 
    relatedArticlesTitles, 
    loading, 
    isSubscriber, 
    newVisitor, 
    newGuest, 
    error, 
    optimizedWidth, 
    supportsAvif, 
    isPrebidConfigured 
  };
}


/* -------------------------------------------------------------------------- */
/*                               MAIN COMPONENT                               */
/* -------------------------------------------------------------------------- */

const ArticlePage = ({ handleModal }) => {
  const { year, month, day, title } = useParams();
  const { article, thumbnail, relatedArticlesTitles, loading, isSubscriber, newVisitor, newGuest, error, optimizedWidth, supportsAvif, isPrebidConfigured } = useFetchArticle({ year, month, day, title });
  const [commentCount, setCommentCount] = useState(0);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [showComponent, setShowComponent] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const { adUnitsRegistered: adsReady } = usePrebid(); 

  // Slight delay for lazy-loaded components
  useEffect(() => {
    const timer = setTimeout(() => setShowComponent(true), 200);
    return () => clearTimeout(timer);
  }, []);

  const handleCommentCountUpdate = (newCount) => {
    setCommentCount(newCount);
  };

  // On thumbnail click → load video iframe
  const loadIframe = () => {
    setIsIframeLoaded(true);
  };

  // Basic paywall rule:
  // - Unauthenticated: 3 free pageviews
  // - Authenticated (non-subscriber): 5 free pageviews
  // - Subscribers / NFT Auth: unlimited

  const canViewFullContent = isSubscriber || newVisitor || newGuest;

  // Parse the article content only when it changes
  const parsedArticleElements = useParsedHtml(article?.content?.rendered);

  /**
   * Generate the final array of React elements that includes:
   * - Ads
   * - RelatedArticlesTitles
   * - Paywall
   * - etc.
   */
  //Memoize all ads, including paragraph ads and native ads

  const isMobile = optimizedWidth <= 480;



  const memoizedAdInsertions = useMemo(() => {
    // don't build anything until prebid/ad units are ready
    if (!adsReady || !isPrebidConfigured) return new Map();

    const adMap = new Map();

    // pass adsReady (adUnitsRegistered) as second arg, isMobile as third
    [3, 14, 30].forEach((paragraph) => {
      adMap.set(paragraph, getArticleAd(paragraph, adsReady, isMobile));
    });

    adMap.set("native", getNativeAd(adsReady));

    return adMap;
  }, [adsReady, isPrebidConfigured, isMobile ]);

  const MIN_PARAS_AFTER_LAST_AD = 4; 
  const AD_POSITIONS_BASE = isMobile ? [3, 14, 30] : [3, 14, 30]; 

  const finalArticleContent = useMemo(() => {
    if (!parsedArticleElements || !article?.title?.rendered) return null;

    // compute children array once here
    const childrenArray = React.Children.toArray(parsedArticleElements);

    // total paragraph count (used for bottom native decision)
    let totalParagraphs = 0;
    for (const el of childrenArray) {
      if (isValidElement(el) && el.type === 'p') totalParagraphs++;
    }

    let paragraphsCount = 0;
    let lastAdParagraph = null;        
    let paywallInserted = false;       
    const elements = [];

    for (let i = 0; i < childrenArray.length; i++) {
      const element = childrenArray[i];
      if (!isValidElement(element)) continue;

      if (element.type === 'p') {
        paragraphsCount++;
      }

      // Always clone and push the element.
      elements.push(cloneElement(element, { key: `paragraph-${i}` }));

      // Insert "Related Articles" after paragraph #7.
      if (element.type === 'p' && paragraphsCount === 7) {
        elements.push(
          <RelatedArticlesTitles
            articles={relatedArticlesTitles}
            key={`related-articles-${i}`}
          />
        );
      }

      // only keep positions that are inside the article and leave enough paras after them
      const filteredAdPositions = AD_POSITIONS_BASE.filter(p => {
        if (p >= totalParagraphs) return false; // beyond end -> skip
        const parasAfter = totalParagraphs - p;
        return parasAfter >= MIN_PARAS_AFTER_LAST_AD;
      });


      // Insert ads for non-paid (non-subscriber/NFT) users at paragraphs 3, 14, 30.
      const shouldConsiderAdHere = filteredAdPositions.includes(paragraphsCount);
      if (shouldConsiderAdHere && memoizedAdInsertions.has(paragraphsCount)) {
        elements.push(memoizedAdInsertions.get(paragraphsCount));
        lastAdParagraph = paragraphsCount;
      }




      // For non-paid users, show a paywall after 2 paragraphs and stop further processing.
      if (!canViewFullContent && paragraphsCount === 2) {
        elements.push(
          <div
            className="text-zinc-800 flex flex-col break-words text-center p-8 rounded"
            key="paywall-message"
          >
            <h3 className="dark:text-white text-[1.50rem] font-medium mb-5"></h3>
            <span
              style={{
                fontSize: '16px',
                fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif',
              }}
              className="text-[0.88rem] mb-10"
            >
              Gain access.
            </span>
            <div>
              <a
                className="w-full md:w-auto bg-b5eae7 text-black custom-link cursor-pointer inline-block text-[0.81rem] font-bold uppercase px-2 md:px-32 py-2 md:py-2"
                style={{
                  fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif',
                  textShadow: 'rgba(0, 0, 0, 0.12) 0px 1px 0px',
                }}
                href="/subscription"
              >
                Read More
              </a>
            </div>
          </div>
        );
        // Break out of the loop to prevent further content from being processed.
        paywallInserted = true;
        break;
      }
    }

    // Append a final ad (for everyone).
    if (memoizedAdInsertions.has("native")) {
      // never append native if a paywall was inserted
      if (!paywallInserted) {
        const paragraphsAfterLastAd = lastAdParagraph === null ? totalParagraphs : (totalParagraphs - lastAdParagraph);
        const shouldAppendNative = paragraphsAfterLastAd >= MIN_PARAS_AFTER_LAST_AD;

        console.info(`[ArticleInsert] native check: total=${totalParagraphs}, lastAd=${lastAdParagraph}, afterLastAd=${paragraphsAfterLastAd}, minRequired=${MIN_PARAS_AFTER_LAST_AD}, paywall=${paywallInserted}, append=${shouldAppendNative}`);

        if (shouldAppendNative) {
          elements.push(memoizedAdInsertions.get("native"));
        }
      } else {
        console.info('[ArticleInsert] skipping native because paywallInserted=true');
      }
    }


    return elements;
  }, [parsedArticleElements, isSubscriber, canViewFullContent, memoizedAdInsertions,relatedArticlesTitles, article?.title?.rendered, optimizedWidth]);

  /* -------------------------------------------------------------------------- */
  /*                                RENDER LOGIC                                */
  /* -------------------------------------------------------------------------- */

  if (loading) {
    return (
      <ArticleSkeleton />
    );
  }

  if (error || !article) {
    return <div>
      <img 
        src={Err}  
        alt="500 error page illustration" 
        style={{ width: '100%', maxWidth: '100%' }}
      />
    </div>;
  }


  const getEmbeddedVideo = (embed = '') => {
    const url = embed.trim();

    // Case 1: iframe string
    if (url.startsWith('<iframe')) {
      return parse(
        url
          .replace(/width="\d+"/g, 'width="100%"')
          .replace(/height="\d+"/g, 'height="100%"')
          .replace(/src="([^"]+)"/, (match, src) => {
            const delimiter = src.includes('?') ? '&' : '?';
            return `src="${src}${delimiter}autoplay=1"`;
          })
      );
    }

    // Case 2: YouTube embed URL (e.g. https://www.youtube.com/embed/XYZ)
    if (url.includes('youtube.com/embed/')) {
      const src = url.includes('?') ? `${url}&autoplay=1` : `${url}?autoplay=1`;
      return (
        <iframe
          width="100%"
          height="100%"
          src={src}
          frameBorder="0"
          allow="autoplay; encrypted-media"
          allowFullScreen
          title="YouTube Video"
        />
      );
    }

    // Case 3: .mp4 file
    if (url.match(/\.(mp4)(\?.*)?$/)) {
      return (
        <video width="100%" height="100%" controls autoPlay>
          <source src={url} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    }

    // Fallback
    return <div>{url}</div>;
  };





  return (
    <>
    <div
      role="main"
      className="grid grid-cols-1 md:grid-cols-3 auto-rows-auto gap-4 text-zinc-600 dark:text-white text-[0.88rem] leading-[1.40rem] mx-1 md:mx-8 px-1 md:px-3.5 mb-8 max-w-screen-xl"
    >
      {/* Title */}
      <h1
        className="col-span-full order-1 row-auto text-neutral-800 dark:text-white text-[2.63rem] font-bold leading-[2.89rem] mt-8 mb-4"
        style={{
          fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif',
          lineHeight: '1.35em',
          fontWeight: '500',
          minHeight: '50px',
        }}
      >
        {article.title?.rendered}
      </h1>

      {/* Date + Comments + Categories (All in One Line) */}
      <div className="col-span-full order-2 row-auto text-neutral-400 dark:text-gray-500 text-[0.81rem] flex items-center flex-wrap gap-4">

               {/* Categories (Below Date & Comments) */}
      {article.categories?.length > 0 && (
        <div className=" col-span-full row-auto flex flex-wrap items-center gap-2 py-2">
          
          {/* SVG Icon for Categories */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-4 h-6 pt-1 "
            >
           
            <path d="M4 14h16" />
            <path d="M4 4h16" />
            <path d="M4 8h10" />

           </svg>

          {/* Category Links */}
          {article.categories
            .filter((category) => !["second", "featured", "third", "fourth",  "fifth", "big image", ].includes(category)) // Exclude certain categories
             .slice(0, optimizedWidth <= 480 ? 2 : 3) 
            .map((category, index) => (
              <Link
                key={index}
                to={`/news/${category}`} // Use category name directly
                className=" !text-gray-500  text-xs  px-1 py-1 uppercase hover:!text-[#FF6600] transition "
              >
                {category}
              </Link>
            ))}
          </div>
        )}
        
        {/* Date */}
         <a className="pl-4 sm:pl-6 md:pl-8 text-gray-500">
         {format(new Date(article.date), 'do MMMM yyyy HH:mm:ss')} UTC</a>

        {/* Comments */}
        <a className="hidden-sm text-gray-500 cursor-pointer pl-8">
          {commentCount === 0
            ? 'No comments'
            : commentCount === 1
            ? '1 Comment'
            : `${commentCount} Comments`}
        </a>
      </div>
      
      {/* Thumbnail/Video */}
      <div className="col-span-full md:col-span-2 order-3 row-auto mt-1 pr-4">
        {article.videoEmbed ? (
          isIframeLoaded ? (
            <>
              <div
                className="relative w-full ml-2 max-w-[900px] h-0"
                style={{ paddingBottom: '50%', minHeight: '400px' }}
              >
                <div className="absolute top-0 left-0 w-full h-full">
                  {article.videoEmbed ? getEmbeddedVideo(article.videoEmbed) : null}
                </div>
              </div>
              
              {article.featuredVideoCaption && (
                <p className="text-neutral-400 dark:text-gray-500 text-[0.69rem] mt-1 mb-1 pr-2 text-right">
                  {article.featuredVideoCaption}
                </p>
              )}
            </>

           ) : (
          <>
            <div onClick={loadIframe} className="relative cursor-pointer">
              <picture>
                {/* Mobile-first AVIF */}
                {supportsAvif && thumbnail?.avifMobile && (
                  <source
                    srcSet={thumbnail.avifMobile}
                    type="image/avif"
                    media="(max-width: 480px)"
                  />
                )}

                {/* AVIF desktop */}
                {supportsAvif && thumbnail?.avif && (
                  <source srcSet={thumbnail.avif} type="image/avif" />
                )}

                {/* WebP fallback */}
                {thumbnail?.webp && (
                  <source srcSet={thumbnail.webp} type="image/webp" />
                )}

                {/* Fallback img */}
                <img
                  className="w-full h-auto ml-2 pr-4"
                  src={thumbnail.fallback || thumbnail.webp || thumbnail.avif}
                  alt={article.imageCaption || 'Video Thumbnail'}
                  width={optimizedWidth || 1200}
                  height={
                    article.thumbnailWidth && article.thumbnailHeight
                      ? (optimizedWidth * article.thumbnailHeight) / article.thumbnailWidth
                      : 628
                  }
                  style={{
                    filter: 'brightness(0.7)',
                    aspectRatio: `${article.thumbnailWidth || 1200} / ${article.thumbnailHeight || 628}`,
                  }}
                  onLoad={() => setIsImageLoaded(true)}
                />
              </picture>
              <div className="absolute inset-0 flex items-center justify-center">
                <span
                  style={{
                    fontSize: '3rem',
                    color: '#fff',
                    textShadow: '0 0 10px rgba(0,0,0,0.5)',
                  }}
                >
                  ▶
                </span>
              </div>
            </div>

            {article.imageCaption && (
              <p className="text-neutral-400 dark:text-gray-500 text-[0.69rem] mt-1 mb-1 pr-2 text-right">
                {article.imageCaption}
              </p>
            )}
          </>
        )
      ) : (
        <div className="w-full">
          {thumbnail ? (
            <picture>
              {/* Mobile-first AVIF */}
              {thumbnail.avifMobile && (
                <source
                  srcSet={thumbnail.avifMobile}
                  type="image/avif"
                  media="(max-width: 480px)"
                />
              )}
              {/* AVIF */}
              {supportsAvif && thumbnail.avif && (
                <source srcSet={thumbnail.avif} type="image/avif" />
              )}
              {/* WebP */}
              {thumbnail.webp && (
                <source srcSet={thumbnail.webp} type="image/webp" />
              )}
              {/* Fallback */}
              <img
                className="w-full h-auto ml-2 pr-4"
                src={thumbnail.fallback || thumbnail.webp || thumbnail.avif}
                alt={article.imageCaption}
                width={optimizedWidth || 1200}
                height={
                  article.thumbnailWidth && article.thumbnailHeight
                    ? (optimizedWidth * article.thumbnailHeight) / article.thumbnailWidth
                    : 628
                }
                style={{
                  aspectRatio: `${article.thumbnailWidth || 1200} / ${article.thumbnailHeight || 628}`,
                }}
                onLoad={() => setIsImageLoaded(true)}
              />
            </picture>
          ) : (
            <p className="text-gray-400">No image available</p>
          )}

          {article.imageCaption && (
            <p className="text-neutral-400 dark:text-gray-500 text-[0.69rem] mt-1 mb-1 pr-2 text-right">
              {article.imageCaption}
            </p>
          )}
        </div>
        )}
      </div>

      {/* Sidebar Placeholder (Reserving Space) */}
      <div className="hidden-md md:col-start-3 order-4 row-auto min-h-[400px]" />

      {/* Article Content */}
      <div className="col-span-full md:col-span-2 order-5 row-auto">
        {article.categories?.includes('opinion') && (
          <h2 className="text-red-500 text-center mb-4 font-semibold">
            Opinion
          </h2>
        )}
        {article.categories?.includes('press release') && (
          <h2 className="text-red-500 text-center mt-4 mb-4 font-semibold">
            Press Release
          </h2>
        )}

        <div
          className="p px-2 sm:p-4 md:px-8 ml-2 text-zinc-800 dark:text-white leading-[1.63rem] break-words"
          style={{
            fontFamily: 'Georgia, Cambria, Arial, serif',
            fontSize: '18px',
          }}
        > 
          {article?.title?.rendered && finalArticleContent}

          {/* NFT Mint (Lazy Loaded) */}
          <ErrorBoundary>
            <Suspense fallback={<div style={{ minHeight: '250px' }} />}>
              {showComponent && (
                <MintArticle
                  articleUrl={`https://www.trustnodes.com/${year}/${month}/${day}/${title}`}
                  title={article.title && he.decode(article.title.rendered)}
                  date={format(parseISO(article.date), 'do MMMM yyyy')}
                  description={article.excerpt && article.excerpt.rendered}
                />
              )}
            </Suspense>
          </ErrorBoundary>

          {/* Comments */}
          <ErrorBoundary>
            <Suspense fallback={<div style={{ minHeight: '50px' }} />}>
              {showComponent && (
                <CommentSection
                  articleId={article.id}
                  handleModal={handleModal}
                  onCommentCountUpdate={handleCommentCountUpdate}
                />
              )}
            </Suspense>
          </ErrorBoundary>
        </div>

        {/* Related Articles */}
        <div className="text-zinc-600 text-[0.88rem] mb-2 mt-4">
          <ErrorBoundary>
            <Suspense fallback={<div style={{ minHeight: '50px' }} />}>
              {showComponent && (
                <RelatedArticles
                  year={year}
                  month={month}
                  day={day}
                  title={title}
                  supportsAvif={supportsAvif}
                />
              )}
            </Suspense>
          </ErrorBoundary>
        </div>
      </div> 

      {/* Ad Placeholder */}
      <div className="hidden-sm md:col-start-3 order-6 row-auto mt-4 pl-4 min-h-[300px]">
        <ErrorBoundary>
          <Suspense fallback={<div style={{ minHeight: '50px' }} />}>
            {!isSubscriber && !isMobile && adsReady && <SidebarAd supportsAvif={supportsAvif} />}

          </Suspense>
        </ErrorBoundary>
      </div>
   
    </div>
   </>
  );
};

export default ArticlePage;
